import React, { useState, useEffect } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  add,
  isToday,
  isSameMonth,
  startOfDay,
} from 'date-fns';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  MapPinIcon,
  EllipsisHorizontalIcon,
  FlagIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import StaffNav from './managernav';
import { useNavigate } from 'react-router-dom';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Shifts({ selectedDate: initialSelectedDate, onBack }) {
    const Navigate = useNavigate();

    const token = localStorage.getItem('token');
  
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(
      initialSelectedDate ? new Date(initialSelectedDate) : null
    );
    const [shifts, setShifts] = useState([]);
    const [updated, setUpdated] = useState(null);
  
    const currentDate = new Date();
  
    const startDate = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
    const endDate = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
  
    const days = [];
    let day = startOfDay(startDate); // Normalize the start day to midnight
  
    while (day <= endDate) {
      days.push(day);
      day = add(day, { days: 1 }); // Move to the next day
    }
  
    const fetchShifts = async (date) => {
      if (!date || !(date instanceof Date)) return; // Safeguard against invalid dates
      try {
        const formattedDate = format(date, 'yyyy-MM-dd');
        const response = await fetch(
          `https://api.insightos.co.uk/api/shiftsday/${formattedDate}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json',
             },
            credentials : 'include',

          }
        );
  
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
  
        const data = await response.json();
        setShifts(data.data);
        setUpdated(null);
      } catch (error) {
        console.error('Error fetching shifts:', error);
      }
    };
  
    // Fetch shifts for the initially selected date
    useEffect(() => {
      if (selectedDate) {
        fetchShifts(selectedDate);
      }
    }, [selectedDate]);
  
    const previousMonth = () => setCurrentMonth(add(currentMonth, { months: -1 }));
    const nextMonth = () => setCurrentMonth(add(currentMonth, { months: 1 }));

  const handleSick = async (id) => {
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/sick/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json',
         },
         credentials : 'include',

      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(data);
      setUpdated('Updated Staff Status');
      fetchShifts(selectedDate);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `https://api.insightos.co.uk/api/deleteshift/${id}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log(data);
      fetchShifts(selectedDate);
      setUpdated('Shift Deleted');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-100 via-sky-50 to-emerald-50 p-2 sm:p-4">
      <div className="mb-3 sm:mb-4 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-3 sm:p-5">
        <h2 className="text-sm sm:text-base font-semibold text-indigo-900">Shift Schedule</h2>
      {onBack && (
        <button
          onClick={onBack}
          type="button"
          className="mt-3 inline-flex w-full sm:w-auto items-center justify-center rounded-xl border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 px-3 py-2 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-rose-900/35 hover:from-rose-500/95 hover:to-red-400/95 transition-all"
        >
          ← Back to Shifts
        </button>
      )}
      {updated && <div className="mt-2 text-xs sm:text-sm font-semibold text-emerald-700">{updated}</div>}
      </div>
      <div className="lg:grid lg:grid-cols-12 lg:gap-x-16">
        {/* Calendar Section */}
        <div className="mt-10 text-center lg:col-start-8 lg:col-end-13 lg:row-start-1 lg:mt-9 xl:col-start-9">
          <div className="rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl shadow-[0_12px_30px_rgba(99,102,241,0.2)] p-2.5 sm:p-4">
          <div className="flex items-center text-indigo-900 gap-2">
            <button
              type="button"
              onClick={previousMonth}
              className="h-7 w-7 sm:h-10 sm:w-10 inline-flex flex-none items-center justify-center rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 shadow-sm hover:bg-white"
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <div className="flex-auto rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/70 px-2 py-1.5 text-[11px] sm:text-sm font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </div>
            <button
              type="button"
              onClick={nextMonth}
              className="h-7 w-7 sm:h-10 sm:w-10 inline-flex flex-none items-center justify-center rounded-lg sm:rounded-xl border border-indigo-200/70 bg-white/75 text-indigo-900 shadow-sm hover:bg-white"
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-7 gap-0.5 sm:gap-1 rounded-xl border border-indigo-200/60 bg-white/60 p-1 text-[10px] sm:text-xs/6 text-indigo-700">
            <div className="p-0.5 sm:p-1">M</div>
            <div className="p-0.5 sm:p-1">T</div>
            <div className="p-0.5 sm:p-1">W</div>
            <div className="p-0.5 sm:p-1">T</div>
            <div className="p-0.5 sm:p-1">F</div>
            <div className="p-0.5 sm:p-1">S</div>
            <div className="p-0.5 sm:p-1">S</div>
          </div>

          <div className="isolate mt-2 grid grid-cols-7 gap-0.5 sm:gap-1 rounded-xl border border-indigo-200/60 bg-white/60 p-1 text-sm shadow-[0_12px_24px_rgba(99,102,241,0.14)]">
            {days.map((day, dayIdx) => (
              <button
                key={day.toISOString()}
                onClick={() => {
                  setSelectedDate(day);
                  fetchShifts(day);
                }}
                type="button"
                className={classNames(
                  'h-7 w-7 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl text-center text-[10px] sm:text-sm font-medium focus:z-10 border transition-all',
                  isSameMonth(day, currentMonth) ? 'bg-white/80 border-indigo-200/70 text-indigo-900 hover:bg-white' : 'bg-white/45 border-transparent text-indigo-400 hover:bg-white/60',
                  isToday(day) && 'ring-2 ring-sky-400 font-bold',
                  selectedDate &&
                    day.toDateString() === selectedDate.toDateString() &&
                    'bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 border-white/20 text-white shadow-lg shadow-indigo-950/35',
                  dayIdx === 0 && 'rounded-tl-lg',
                  dayIdx === 6 && 'rounded-tr-lg',
                  dayIdx === days.length - 7 && 'rounded-bl-lg',
                  dayIdx === days.length - 1 && 'rounded-br-lg'
                )}
              >
                <time
                  dateTime={day.toISOString()}
                  className="mx-auto flex size-6 sm:size-7 items-center justify-center rounded-full"
                >
                  {format(day, 'd')}
                </time>
              </button>
            ))}
          </div>
          </div>
        </div>

        {/* Shifts Section */}
        <ol className="mt-4 space-y-3 sm:space-y-4 text-sm/6 lg:col-span-7 xl:col-span-8">
          {shifts.length > 0 ? (
            shifts.map((shift, idx) => (
              <li
                key={idx}
                className="relative mt-2 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-purple-100/80 backdrop-blur-xl p-3 sm:p-4 shadow-[0_12px_30px_rgba(99,102,241,0.18)]"
              >
                <div className="flex-auto">
                  <h3 className="pr-2 font-semibold text-indigo-900 text-sm sm:text-base">
                    {shift.Name}
                  </h3>
                  <dl className="mt-2 flex flex-col text-indigo-800 xl:flex-row gap-2 xl:gap-0">
                    <div className="flex items-start space-x-3">
                      <dt className="mt-0.5">
                        <span className="sr-only">Shift</span>
                        <CalendarIcon
                          className="size-4 sm:size-5 text-indigo-700"
                          aria-hidden="true"
                        />
                      </dt>
                      <dd className="text-indigo-900 text-xs sm:text-sm font-semibold">
                        {shift.StartTime} - {shift.EndTime}
                      </dd>
                    </div>

                    <div className="mt-2 flex items-start space-x-3 xl:ml-3.5 xl:mt-0 xl:border-l xl:border-gray-400/50 xl:pl-3.5">
                      <dt className="mt-0.5">
                        <span className="sr-only">Status</span>
                        <MapPinIcon
                          className="size-4 sm:size-5 text-sky-700"
                          aria-hidden="true"
                        />
                      </dt>
                      <dd className="text-xs sm:text-sm font-semibold text-indigo-900">
                        {shift.Sick
                          ? 'Sick'
                          : shift.Holiday
                          ? 'Holiday'
                          : shift.Late
                          ? 'Late'
                          : 'On Time'}
                      </dd>
                    </div>
                  </dl>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => handleSick(shift.id)}
                      type="button"
                      className="inline-flex items-center gap-x-1.5 rounded-lg sm:rounded-xl border border-white/20 bg-gradient-to-br from-indigo-900/90 to-indigo-700/90 px-2 py-1 sm:px-2.5 sm:py-1.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-indigo-950/35 hover:from-indigo-800/95 hover:to-indigo-600/95 transition-all"
                    >
                      Sick
                      <FlagIcon
                        aria-hidden="true"
                        className="-mr-0.5 size-5"
                      />
                    </button>
                    <button
                      onClick={() => handleDelete(shift.id)}
                      type="button"
                      className="inline-flex items-center gap-x-1.5 rounded-lg sm:rounded-xl border border-white/20 bg-gradient-to-br from-rose-600/90 to-red-500/90 px-2 py-1 sm:px-2.5 sm:py-1.5 text-xs sm:text-sm font-semibold text-white shadow-lg shadow-rose-900/35 hover:from-rose-500/95 hover:to-red-400/95 transition-all"
                    >
                      Cancel Shift
                      <XCircleIcon
                        aria-hidden="true"
                        className="-mr-0.5 size-5"
                      />
                    </button>
                  </div>
                </div>

                <div className="absolute right-3 top-3 xl:static xl:ml-3">
                  <EllipsisHorizontalIcon
                    className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-300"
                    aria-hidden="true"
                  />
                </div>
              </li>
            ))
          ) : (
            <div className="rounded-xl border border-indigo-200/60 bg-white/70 p-3 text-xs sm:text-sm font-medium text-indigo-800">No shifts for this day.</div>
          )}
        </ol>
      </div>
    </div>
  );
}
